import * as d3 from 'd3';

export const parseDate = (dateStr) => {
  const datePart = dateStr.split('T')[0];
  const [year, month, day] = datePart.split('-');
  return new Date(year, month - 1, day);
};

export const parseDateWithHours = (dateStr) => {
  const [datePart, timePart] = dateStr.split('T');
  const [year, month, day] = datePart.split('-');
  const [hour] = timePart.split(':');
  return new Date(year, month - 1, day, hour);
};

export const twoSimbols = (val) =>
  val.toString().length === 1 ? `0${val}` : val;
export const formatDate = (d) =>
  `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
export const formatTime = (d) =>
  `${twoSimbols(d.getHours())}:${twoSimbols(d.getMinutes())}`;

export const getEdgeDates = (datesList) => {
  const start = datesList[0]
    ? parseDateWithHours(datesList[0].created_at)
    : new Date();
  start.setDate(start.getDate() - 1);
  const end = datesList[datesList.length - 1]
    ? parseDateWithHours(datesList[datesList.length - 1].created_at)
    : new Date();
  return [start, end];
};

export const renderRootSvg = (parentHtmlElement, sizes) => {
  return d3
    .select(parentHtmlElement)
    .html('')
    .append('svg')
    .attr('width', sizes.width + sizes.margin.left + sizes.margin.right)
    .attr('height', sizes.height + sizes.margin.top + sizes.margin.bottom)
    .append('g')
    .attr(
      'transform',
      'translate(' + sizes.margin.left + ',' + sizes.margin.top + ')',
    );
};

export const calcSizes = (containerWidth, containerHeight) => {
  const margin = { top: 30, right: 30, bottom: 70, left: 60 };
  const width = containerWidth - margin.left - margin.right;
  const height = containerHeight - margin.top - margin.bottom;

  return {
    margin,
    width,
    height,
  };
};

export const makeDatesListAndFormatters = (startDate, endDate) => {
  let datesList = d3.timeDays(startDate, endDate);
  let formatFn = formatDate;
  let parseFn = parseDate;

  if (datesList.length < 2) {
    startDate.setHours(0);
    endDate.setHours(23);
    datesList = d3.timeHours(startDate, endDate);
    formatFn = formatTime;
    parseFn = parseDateWithHours;

    return { datesList, formatFn, parseFn };
  } else if (datesList.length > 365) {
    // TODO: create for year
    return { datesList, formatFn, parseFn };
  }

  return { datesList, formatFn, parseFn };
};

export const renderXScale = (svg, datesList, formatFn, sizes) => {
  const x = d3
    .scaleBand()
    .range([0, sizes.width])
    .domain(datesList.map(formatFn));

  const xAxis = (g, xVal) => g.call(d3.axisBottom(x));

  svg
    .append('g')
    .attr('transform', 'translate(0,' + sizes.height + ')')
    .call(xAxis)
    .selectAll('text')
    .attr('transform', 'translate(-10,0)rotate(-15)')
    .style('text-anchor', 'end');

  return { x };
};

export const renderYScale = (svg, data, sizes) => {
  const rolled = d3.rollup(
    data,
    (D) => D.length,
    (d) => parseDate(d.created_at).getTime(),
  );
  const maxVal = Math.max(...rolled.values(), 1);

  const y = d3
    .scaleLinear()
    .domain([0, maxVal])
    .rangeRound([sizes.height, 0])
    .clamp(true);

  svg.append('g').call(d3.axisLeft(y).ticks(maxVal));

  return { y, maxVal };
};

export const renderData = (
  svg,
  sizes,
  datesList,
  x,
  y,
  formatFn,
  parseFn,
  data,
) => {
  return svg
    .append('g')
    .attr('fill', 'steelblue')
    .selectAll()
    .data(datesList)
    .join('rect')
    .attr('x', (d) => x(formatFn(d)))
    .attr('width', (d) => Math.floor(sizes.width / (x.domain().length + 1)) - 1)
    .attr('y', (d) => {
      const onDate = data.filter(
        (dataItem) => parseFn(dataItem.created_at).getTime() === d.getTime(),
      );
      return y(onDate.length);
    })
    .attr('height', (d) => {
      const onDate = data.filter(
        (dataItem) => parseFn(dataItem.created_at).getTime() === d.getTime(),
      );
      return y(0) - y(onDate.length);
    });
};

export const renderTooltip = (
  parentHtmlElement,
  sizes,
  data,
  parseFn,
  rect,
) => {
  const Tooltip = d3
    .select(parentHtmlElement)
    .append('div')
    .style('opacity', 0)
    .attr('class', 'tooltip')
    .style('background-color', 'white')
    .style('border', 'solid')
    .style('border-width', '2px')
    .style('border-radius', '5px')
    .style('padding', '5px');

  rect
    .attr('data-value', (d) => {
      const onDate = data.filter(
        (dataItem) => parseFn(dataItem.created_at).getTime() === d.getTime(),
      );
      return onDate.length;
    })
    .on('mouseleave', function (d) {
      Tooltip.style('opacity', 0);
    })
    .on('mousemove', function (d) {
      Tooltip.style(
        'left',
        d3.pointer(d)[0] + sizes.margin.left + sizes.margin.right + 30 + 'px',
      ).style(
        'top',
        d3.pointer(d)[1] + sizes.margin.top + sizes.margin.bottom + 30 + 'px',
      );
    })
    .on('mouseover', function (d) {
      Tooltip.html(d.target.dataset.value)
        .style('position', 'absolute')
        .style('opacity', 1);
    });

  return rect;
};

export const applyZoomBehavior = (svg, sizes) => {
  // TODO: create zoom function
  // Create the zoom behavior.
  // const zoom = d3.zoom()
  //     .scaleExtent([1, 32])
  //     .extent([[sizes.margin.left, 0], [sizes.width - sizes.margin.right, sizes.height]])
  //     .translateExtent([[sizes.margin.left, -Infinity], [sizes.width - sizes.margin.right, Infinity]])
  //     .on("zoom", zoomed);
  // // When zooming, redraw the area and the x axis.
  // function zoomed(event) {
  //     const t = event.transform;
  //     xEl.attr("transform", d3.zoomIdentity.translate(t.x, sizes.height).scale(t.k));
  //     // const xz = event.transform.rescaleX(x);
  //     // console.log(event);
  //     // console.log(xz);
  //     // path.attr("d", area(data, xz));
  //     // gx.call(xAxis, xz);
  // }
  // // Initial zoom.
  // svg.call(zoom)
  //     .transition()
  //         .duration(750)
  //         .call(zoom.scaleTo, 4, [x(Date.UTC(2001, 8, 1)), 0]);
};
